
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useAuthSession } from '@/hooks/useAuthSession';
import { supabase } from '@/lib/supabase';
import { Link, useNavigate } from 'react-router-dom';
import { 
  AlertCircle,
  ArrowLeft,
  CheckCircle
} from "lucide-react";
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { checkAffiliateRequestStatus, checkCanBeAffiliate } from '@/utils/AffiliateUtils';

export default function ApplyPage() {
  const { session, isLoading } = useAuthSession();
  const navigate = useNavigate();
  
  const [referralCodeBase, setReferralCodeBase] = useState('');
  const [upiId, setUpiId] = useState('');
  const [paytmNumber, setPaytmNumber] = useState('');
  const [bankDetails, setBankDetails] = useState({
    accountNumber: '',
    bankName: '',
    ifscCode: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEligible, setIsEligible] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  
  // Check eligibility when component mounts
  useEffect(() => {
    const checkEligibility = async () => {
      if (!session?.user) return;
      
      setIsChecking(true);
      
      try {
        // Check if user can be an affiliate
        const canBeAffiliate = await checkCanBeAffiliate(session.user.id);
        
        if (canBeAffiliate) {
          setIsEligible(true);
          setIsChecking(false);
          return;
        }
        
        // If not explicitly allowed, check if they have an approved request
        const requestStatus = await checkAffiliateRequestStatus(session.user.id);
        
        if (requestStatus === 'approved') {
          setIsEligible(true);
        } else if (requestStatus === 'pending') {
          setError("Your application to join the affiliate program is still pending approval.");
        } else if (requestStatus === 'rejected') {
          setError("Your application to join the affiliate program was not approved.");
        } else {
          setError("You need to request to join the affiliate program before you can apply.");
        }
      } catch (err) {
        console.error('Error checking eligibility:', err);
        setError("Failed to check eligibility status. Please try again later.");
      } finally {
        setIsChecking(false);
      }
    };
    
    checkEligibility();
  }, [session?.user]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!session?.user) {
      toast.error("You must be logged in to apply");
      navigate("/auth");
      return;
    }
    
    if (!referralCodeBase.trim()) {
      setError("Please provide a referral code base");
      return;
    }
    
    // Validate payment details - at least one method required
    const hasPaymentDetails = 
      upiId.trim() || 
      paytmNumber.trim() || 
      (bankDetails.accountNumber && bankDetails.bankName && bankDetails.ifscCode);
    
    if (!hasPaymentDetails) {
      setError("Please provide at least one payment method");
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Call the edge function to handle the application submission
      const { data: responseData, error: edgeFunctionError } = await supabase.functions.invoke(
        'submit-affiliate-application',
        {
          body: {
            userId: session.user.id,
            referralCodeBase,
            bankDetails: bankDetails.accountNumber ? bankDetails : undefined,
            upiId: upiId || undefined,
            paytmNumber: paytmNumber || undefined
          }
        }
      );
      
      if (edgeFunctionError) {
        console.error('Error submitting affiliate application:', edgeFunctionError);
        setError(`Application submission failed: ${edgeFunctionError.message}`);
        toast.error("Failed to submit application");
        return;
      }
      
      if (responseData?.error) {
        console.error('Application submission error response:', responseData);
        setError(`${responseData.error}`);
        toast.error("Failed to submit application");
        return;
      }
      
      toast.success("Application submitted successfully!");
      navigate("/affiliate/dashboard");
    } catch (error: any) {
      console.error('Error in affiliate application:', error);
      setError(`Application submission failed: ${error.message}`);
      toast.error("An unexpected error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (isLoading || isChecking) {
    return (
      <div className="container max-w-xl py-8">
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="flex flex-col items-center space-y-2">
              <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
              <p className="text-sm text-muted-foreground">{isLoading ? "Loading..." : "Checking eligibility..."}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  if (!session) {
    return (
      <div className="container max-w-xl py-8">
        <Card>
          <CardContent className="py-8">
            <div className="text-center">
              <p>Please sign in to apply for the affiliate program.</p>
              <Button asChild className="mt-4">
                <Link to="/auth">Sign In</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  if (!isEligible) {
    return (
      <div className="container max-w-xl py-8">
        <Card>
          <CardHeader>
            <CardTitle>Application Required</CardTitle>
            <CardDescription>
              You need to be approved to apply as an affiliate
            </CardDescription>
          </CardHeader>
          <CardContent className="py-6">
            <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-6">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-amber-500 mr-2" />
                <p className="text-amber-700">{error || "You need to request to join the affiliate program first."}</p>
              </div>
            </div>
            
            <div className="flex justify-center mt-4">
              <Button asChild>
                <Link to="/affiliate/request">Request to Join</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="container max-w-xl py-8">
      <Button variant="ghost" className="mb-4" asChild>
        <Link to="/affiliate/dashboard">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Dashboard
        </Link>
      </Button>
      
      <Card>
        <CardHeader>
          <CardTitle>Apply to Become an Affiliate</CardTitle>
          <CardDescription>
            Share Edaptica with others and earn commissions on subscriptions
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
                <p className="text-red-700">{error}</p>
              </div>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="referral-code">Preferred Referral Code Base</Label>
              <Input
                id="referral-code"
                placeholder="yourname"
                value={referralCodeBase}
                onChange={(e) => setReferralCodeBase(e.target.value)}
                className="w-full"
              />
              <p className="text-xs text-muted-foreground">
                Enter a simple text that you'd like to use as your referral code. 
                We'll create a unique code based on this.
              </p>
            </div>
            
            <div className="border-t pt-4">
              <h3 className="font-medium text-lg mb-4">Payment Details</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Please provide at least one payment method where we can send your commissions.
              </p>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="upi">UPI ID</Label>
                  <Input
                    id="upi"
                    placeholder="yourname@upi"
                    value={upiId}
                    onChange={(e) => setUpiId(e.target.value)}
                  />
                </div>
                
                <div>
                  <Label htmlFor="paytm">Paytm Number</Label>
                  <Input
                    id="paytm"
                    placeholder="Your Paytm number"
                    value={paytmNumber}
                    onChange={(e) => setPaytmNumber(e.target.value)}
                  />
                </div>
                
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-2">Bank Account Details (Optional)</h4>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="account-number">Account Number</Label>
                      <Input
                        id="account-number"
                        placeholder="Your bank account number"
                        value={bankDetails.accountNumber}
                        onChange={(e) => setBankDetails(prev => ({ ...prev, accountNumber: e.target.value }))}
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="bank-name">Bank Name</Label>
                      <Input
                        id="bank-name"
                        placeholder="Your bank name"
                        value={bankDetails.bankName}
                        onChange={(e) => setBankDetails(prev => ({ ...prev, bankName: e.target.value }))}
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="ifsc">IFSC Code</Label>
                      <Input
                        id="ifsc"
                        placeholder="IFSC Code"
                        value={bankDetails.ifscCode}
                        onChange={(e) => setBankDetails(prev => ({ ...prev, ifscCode: e.target.value }))}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <Button 
              type="submit" 
              className="w-full" 
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <><div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent mr-2" /> Submitting...</>
              ) : (
                <><CheckCircle className="h-4 w-4 mr-2" /> Submit Application</>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
